"use client";

import { MindstringPrototype } from "@/generated/fastapi";
import {
  MEILI_SEARCH_CONFIG,
  MemoizedSearchIndex,
  MyInfiniteHitsGeneric,
  usePreservedLoadedPageCount,
} from "@/features/search";
import {
  useSearchQuerySelect,
  useSelectSearchControls,
} from "@/state/globalStore";
import appSettings from "@/shared/config/appSettings";
import { PROFILE_SHAPING_EMPTY_STATES } from "@/features/profile-shaping/config/actions";
import MyInfiniteHitThought from "../components/MyInfiniteHitThought";
import { useStudioIndexHits } from "../contexts/StudioIndexHitsContext";
import DeferredSearchIndexMount from "./DeferredSearchIndexMount";

type StudioFreeListSearchPanelProps = {
  prototype: MindstringPrototype;
};

export default function StudioFreeListSearchPanel({
  prototype,
}: StudioFreeListSearchPanelProps) {
  const searchConfig = MEILI_SEARCH_CONFIG.thoughts;
  const searchControls = useSelectSearchControls();
  const searchQueryState = useSearchQuerySelect();
  const { registerIndexHits, unregisterIndexHits } = useStudioIndexHits();
  const indexId = `freelist-confirmed-${prototype}`;
  const preserveLoadedPagesKey = [
    indexId,
    searchQueryState.searchQuery,
    searchControls.hitsPerPage,
    searchControls.sortBy,
    searchControls.semanticRatio,
    searchControls.filters.language.join(","),
  ].join("|");
  const { loadedPageCount, loadMore } = usePreservedLoadedPageCount(
    preserveLoadedPagesKey,
  );

  return (
    <DeferredSearchIndexMount
      prototype={prototype}
      enableIntersectionObserver={true}
      showLoadingSpinner={true}
    >
      <MemoizedSearchIndex
        indexName={searchConfig.indexName}
        indexId={indexId}
        prototype={prototype}
        hitsPerPage={searchControls.hitsPerPage * loadedPageCount}
        mainContentField={searchConfig.mainContentField}
        previewLength={appSettings.search.SEARCH_RESULT_PREVIEW_LENGTH}
        sortBy={searchControls.sortBy}
        languageFilters={searchControls.filters.language}
      >
        <MyInfiniteHitsGeneric
          config={searchConfig}
          renderHit={MyInfiniteHitThought}
          indexId={indexId}
          onIndexHitsChange={registerIndexHits}
          onIndexHitsUnmount={unregisterIndexHits}
          onLoadMoreAction={loadMore}
          emptyState={{
            ...PROFILE_SHAPING_EMPTY_STATES.confirmedThoughts,
            icon: "confirmed_thought",
          }}
        />
      </MemoizedSearchIndex>
    </DeferredSearchIndexMount>
  );
}
